import { lazy, Suspense } from "react";
import { BrowserRouter, HashRouter, Navigate, Route, Routes, useLocation } from "react-router-dom";
import { QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "sonner";
import { queryClient } from "@/lib/query";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import AppShell from "@/components/layout/AppShell";
import { PageLoader } from "@/components/common";
import { IS_PREVIEW } from "@/lib/api";
import PreviewBanner from "@/components/layout/PreviewBanner";
import ErrorBoundary from "@/components/layout/ErrorBoundary";

import Login from "@/pages/auth/Login";
import Signup from "@/pages/auth/Signup";
import ForgotPassword from "@/pages/auth/ForgotPassword";
import ResetPassword from "@/pages/auth/ResetPassword";
import AcceptInvite from "@/pages/auth/AcceptInvite";
import Landing from "@/pages/Landing";

const Dashboard = lazy(() => import("@/pages/Dashboard"));
const Analytics = lazy(() => import("@/pages/Analytics"));
const Inbox = lazy(() => import("@/pages/Inbox"));
const Contacts = lazy(() => import("@/pages/Contacts"));
const ContactDetail = lazy(() => import("@/pages/ContactDetail"));
const Segments = lazy(() => import("@/pages/Segments"));
const Campaigns = lazy(() => import("@/pages/Campaigns"));
const CampaignNew = lazy(() => import("@/pages/CampaignNew"));
const CampaignDetail = lazy(() => import("@/pages/CampaignDetail"));
const Templates = lazy(() => import("@/pages/Templates"));
const AutoReplies = lazy(() => import("@/pages/automation/AutoReplies"));
const Flows = lazy(() => import("@/pages/automation/Flows"));
const FlowBuilder = lazy(() => import("@/pages/automation/FlowBuilder"));
const AIAssistant = lazy(() => import("@/pages/automation/AIAssistant"));
const InstagramAutomation = lazy(() => import("@/pages/automation/InstagramAutomation"));
const Sequences = lazy(() => import("@/pages/automation/Sequences"));
const AIStudio = lazy(() => import("@/pages/automation/AIStudio"));
const ChatLinks = lazy(() => import("@/pages/ChatLinks"));
const Pipeline = lazy(() => import("@/pages/Pipeline"));
const Tasks = lazy(() => import("@/pages/Tasks"));
const SettingsLayout = lazy(() => import("@/pages/settings/SettingsLayout"));

function Protected({ children }) {
  const { user } = useAuth();
  const location = useLocation();
  if (user === undefined) return <PageLoader label="Signing you in…" />;
  if (!user) return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  return (
    <AppShell>
      <ErrorBoundary resetKey={location.pathname}>
        <Suspense fallback={<PageLoader />}>{children}</Suspense>
      </ErrorBoundary>
    </AppShell>
  );
}

function PublicOnly({ children }) {
  const { user } = useAuth();
  if (user === undefined) return <PageLoader />;
  if (user) return <Navigate to="/dashboard" replace />;
  return children;
}

const protectedRoutes = [
  ["/dashboard", Dashboard],
  ["/analytics", Analytics],
  ["/inbox", Inbox],
  ["/inbox/:conversationId", Inbox],
  ["/contacts", Contacts],
  ["/contacts/:id", ContactDetail],
  ["/segments", Segments],
  ["/campaigns", Campaigns],
  ["/campaigns/new", CampaignNew],
  ["/campaigns/:id", CampaignDetail],
  ["/campaigns/:id/edit", CampaignNew],
  ["/templates", Templates],
  ["/automation", () => <Navigate to="/automation/auto-replies" replace />],
  ["/automation/auto-replies", AutoReplies],
  ["/automation/flows", Flows],
  ["/automation/flows/:id", FlowBuilder],
  ["/automation/ai", AIAssistant],
  ["/automation/instagram", InstagramAutomation],
  ["/automation/sequences", Sequences],
  ["/automation/studio", AIStudio],
  ["/links", ChatLinks],
  ["/pipeline", Pipeline],
  ["/tasks", Tasks],
  ["/settings/*", SettingsLayout],
];

// The published preview is served from a sub-path, so it uses hash routing.
const Router = IS_PREVIEW ? HashRouter : BrowserRouter;

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <Router>
          {IS_PREVIEW && <PreviewBanner />}
          <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/login" element={<PublicOnly><Login /></PublicOnly>} />
            <Route path="/signup" element={<PublicOnly><Signup /></PublicOnly>} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/accept-invite" element={<AcceptInvite />} />
            {protectedRoutes.map(([path, Page]) => (
              <Route key={path} path={path} element={<Protected><Page /></Protected>} />
            ))}
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Routes>
          <Toaster position="top-right" richColors closeButton toastOptions={{ style: { fontFamily: "Instrument Sans, sans-serif" } }} />
        </Router>
      </AuthProvider>
    </QueryClientProvider>
  );
}

export default App;
